import { useState, useRef } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark, oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { useTheme } from "@/context/ThemeContext";
import { Copy, Check, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const LANG_MAP = {
  js: "javascript", javascript: "javascript", jsx: "jsx", ts: "typescript",
  py: "python", python: "python",
  html: "markup", css: "css", sql: "sql", matlab: "matlab",
  json: "json", bash: "bash", sh: "bash",
};

const COLLAPSE_LINE_THRESHOLD = 18;

function detectLanguage(code, hinted) {
  if (hinted) {
    const k = hinted.toLowerCase().trim();
    return LANG_MAP[k] || k;
  }
  const c = (code || "").slice(0, 400);
  if (/^\s*<!DOCTYPE|<html|<div/i.test(c)) return "markup";
  if (/\b(SELECT|FROM|WHERE|INSERT|UPDATE)\b/i.test(c)) return "sql";
  if (/\b(def |import |print\(|self\.)/.test(c)) return "python";
  if (/\b(function|const |let |=>|require\()/.test(c)) return "javascript";
  return "javascript";
}

export function CodeBlock({ code, language, className = "", showLineNumbers = true }) {
  const { theme } = useTheme();
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef(null);

  const clean = (code || "").replace(/\s+$/g, "");
  const lang = detectLanguage(clean, language);
  const lineCount = clean.split("\n").length;
  const isLong = lineCount > COLLAPSE_LINE_THRESHOLD;

  const isDark = theme === "dark";
  const style = isDark ? oneDark : oneLight;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(clean);
      setCopied(true);
      toast.success("Code copied");
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div
      ref={containerRef}
      data-testid="code-block"
      className={`group relative overflow-hidden rounded-xl border border-border bg-secondary/40 ${className}`}
    >
      <div className="flex items-center justify-between border-b border-border/60 bg-background/40 px-3 py-1.5">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-destructive/50" />
          <span className="h-2 w-2 rounded-full bg-accent/50" />
          <span className="h-2 w-2 rounded-full bg-primary/50" />
          <span className="ml-2 label-mono">{lang || "code"}</span>
        </div>
        <div className="flex items-center gap-1">
          {isLong && (
            <button
              data-testid="code-block-expand"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1 rounded px-2 py-1 font-mono text-[0.65rem] uppercase tracking-widest text-muted-foreground hover:text-primary transition"
              aria-label={expanded ? "Collapse" : "Expand"}
            >
              {expanded ? <><ChevronUp className="h-3 w-3" /> collapse</> : <><ChevronDown className="h-3 w-3" /> expand</>}
            </button>
          )}
          <button
            data-testid="code-block-copy"
            onClick={copy}
            className="inline-flex items-center gap-1 rounded px-2 py-1 font-mono text-[0.65rem] uppercase tracking-widest text-muted-foreground hover:text-primary transition"
            aria-label="Copy code"
          >
            {copied ? <><Check className="h-3 w-3" /> copied</> : <><Copy className="h-3 w-3" /> copy</>}
          </button>
        </div>
      </div>
      <div
        className={`overflow-auto transition-all ${!expanded && isLong ? "max-h-[280px]" : "max-h-[70vh]"}`}
      >
        <SyntaxHighlighter
          language={lang}
          style={style}
          showLineNumbers={showLineNumbers}
          wrapLongLines={false}
          customStyle={{
            margin: 0,
            padding: "12px 16px",
            background: "transparent",
            fontSize: "12.5px",
            fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            lineHeight: 1.55,
          }}
          lineNumberStyle={{
            minWidth: "2.2em",
            paddingRight: "12px",
            color: "hsl(var(--muted-foreground) / 0.4)",
            fontSize: "11px",
            userSelect: "none",
          }}
        >
          {clean}
        </SyntaxHighlighter>
      </div>
    </div>
  );
}

export default CodeBlock;
